import React from 'react';
import type { TransformedReport } from '../types';
import AvatarSvg, { SEGMENT_COLORS } from './AvatarSvg';

interface OverviewSlideProps {
  data: TransformedReport;
  highlightColumn?: 'usp' | 'who' | 'insight-what' | 'all';
  highlightSegmentRow?: number;
  showLockOverlay?: boolean;
}

/**
 * OverviewSlide - The 10-column overview grid (reused 5 times with different highlights).
 * Maps USP -> WHO (3 segments) -> INSIGHT -> WHAT
 */
const OverviewSlide: React.FC<OverviewSlideProps> = ({
  data,
  highlightColumn = 'all',
  highlightSegmentRow,
  showLockOverlay = false,
}) => {
  const segments = data.segments ?? [];
  const strategies = data.appeal_strategies ?? [];
  const usp = data.usp;

  // Derive per-segment insights and WHAT data
  const segmentData = segments.map((seg, si) => {
    const insights = [
      seg.emotion_map?.pain || '',
      seg.emotion_map?.barriers || '',
      seg.emotion_map?.purchase_drivers || '',
    ].filter(Boolean);

    // Find strategies for this segment (fallback: use strategies in order)
    const segStrategies = strategies.filter((s) => s.segment_id === seg.segment_id);
    const effectiveStrategies = segStrategies.length > 0
      ? segStrategies
      : si === 0
        ? strategies.slice(0, 3)
        : [];

    return {
      segment: seg,
      insights,
      strategies: effectiveStrategies,
    };
  });

  // Determine dimming based on highlightColumn
  const isUspDimmed = highlightColumn !== 'all' && highlightColumn !== 'usp';
  const isWhoDimmed = highlightColumn !== 'all' && highlightColumn !== 'who';
  const isInsightWhatDimmed = highlightColumn !== 'all' && highlightColumn !== 'insight-what';

  // Header opacity based on highlight
  const hdOpacity = (col: 'usp' | 'who' | 'insight' | 'what') => {
    if (highlightColumn === 'all') return 1;
    if (highlightColumn === 'usp') return col === 'usp' ? 1 : 0.3;
    if (highlightColumn === 'who') return col === 'who' ? 1 : 0.3;
    if (highlightColumn === 'insight-what') return (col === 'insight' || col === 'what') ? 1 : 0.3;
    return 1;
  };

  // Title and subtitle based on variant
  const getHeader = () => {
    switch (highlightColumn) {
      case 'usp':
        return { label: 'Step 01', title: 'USP（自社の強み）の整理', subtitle: 'まず、なぜこの強みを定義したのかをご説明します。' };
      case 'who':
        return { label: 'Step 02', title: 'WHO：届ける相手の整理', subtitle: 'この強みを誰に届けるかをご説明します' };
      case 'insight-what':
        return { label: 'Step 03 \u2014 WHAT', title: 'このホンネに、何を伝えるか', subtitle: '整理したインサイトをもとに、訴求戦略を設計します' };
      default:
        if (showLockOverlay) {
          return { label: 'Summary', title: 'もう一度、全体像に戻ります', subtitle: '本レポートでご説明した範囲と、残りのご提案について' };
        }
        return { label: 'Overview', title: 'Taskyが考える、クリエイティブ戦略の全体像', subtitle: 'URLを元に、「誰のインサイトに」「何を伝えるか」を整理しました。' };
    }
  };

  const header = getHeader();

  // Determine which segment rows are dimmed
  const isSegRowDimmed = (rowIdx: number) => {
    if (highlightColumn === 'insight-what' && highlightSegmentRow !== undefined) {
      return rowIdx !== highlightSegmentRow;
    }
    if (showLockOverlay && rowIdx > 0) return true;
    return false;
  };

  // For the USP highlight variant, apply a green border
  const uspBorderStyle = highlightColumn === 'usp'
    ? { border: '6px solid #3eb298' }
    : {};

  // Grid rows for segments: row 3, 5, 7
  const segGridRows = [3, 5, 7];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="slide-hdr" style={{ paddingRight: '80px' }}>
        <p className="sl">{header.label}</p>
        <h2>{header.title}</h2>
        <p className="bt">{header.subtitle}</p>
      </div>

      <div className="ov" style={{ position: 'relative' }}>
        {/* Header row: 6 cells */}
        <div className="ov-hd" style={{ opacity: hdOpacity('usp') }}>USP（自社の強み）</div>
        <div className="ov-hd" />
        <div className="ov-hd" style={{ opacity: hdOpacity('who') }}>WHO（誰に届けるか）</div>
        <div className="ov-hd" style={{ opacity: hdOpacity('insight') }}>INSIGHT（相手の本音・気づき）</div>
        <div className="ov-hd" />
        <div className="ov-hd" style={{ opacity: hdOpacity('what') }}>WHAT（何を伝えるか？）</div>

        {/* Spacer */}
        <div className="ov-sp" />

        {/* Arrow between USP and WHO */}
        <div
          className="ov-gap-arw"
          style={{
            gridRow: '3/8',
            opacity: highlightColumn === 'all' ? 1 : 0.15,
          }}
        >
          &#9654;
        </div>

        {/* USP card: spans rows 3-7 */}
        <div
          className={`ov-usp ${isUspDimmed ? 'lock-dim' : ''}`}
          style={{ gridRow: '3/8', ...uspBorderStyle }}
        >
          <p className="ov-usp-label font-semibold" style={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', color: 'rgba(29,25,38,0.4)' }}>
            USP
          </p>
          <p className="ov-usp-title font-bold" style={{ lineHeight: 1.4 }}>
            {usp.usp_statement || ''}
          </p>
          <ul className="ov-usp-list">
            {(usp.pod ?? []).map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>

        {/* Segment rows */}
        {segmentData.map((sd, si) => {
          const gridRow = segGridRows[si];
          if (!gridRow) return null;
          const dimmed = isSegRowDimmed(si);

          // Determine dimming per column
          const segDim = dimmed || isWhoDimmed;
          const insDim = dimmed || isInsightWhatDimmed;
          const arwDim = dimmed || isInsightWhatDimmed;
          const whtDim = dimmed || isInsightWhatDimmed;

          // For insight-what highlight with a specific segment row, WHO is also dimmed
          const segActualDim = highlightColumn === 'insight-what' ? (dimmed ? true : true) : segDim;
          const insActualDim = highlightColumn === 'insight-what' ? dimmed : insDim;
          const arwActualDim = highlightColumn === 'insight-what' ? dimmed : arwDim;
          const whtActualDim = highlightColumn === 'insight-what' ? dimmed : whtDim;

          return (
            <React.Fragment key={si}>
              {/* Gap before segments 2, 3 */}
              {si > 0 && (
                <div className="ov-sgap" style={{ gridRow: gridRow - 1 }} />
              )}

              {/* WHO segment */}
              <div
                className={`ov-seg ${segActualDim ? 'lock-dim' : ''}`}
                style={{ gridRow }}
              >
                <span className="ov-badge">{sd.segment.funnel_stage || ''}</span>
                <div className="ov-who-name">
                  <div className="ov-avatar">
                    <AvatarSvg variant={si} color={SEGMENT_COLORS[si]} />
                  </div>
                  <p style={{ fontSize: 'clamp(11px, 1.9vmin, 15px)', fontWeight: 700, lineHeight: 1.4 }}>
                    {sd.segment.name || ''}
                  </p>
                </div>
                {!dimmed && (
                  <p style={{ fontSize: 'clamp(8px, 1.2vmin, 11px)', color: 'rgba(29,25,38,0.4)', lineHeight: 1.5 }}>
                    {[sd.segment.demographics?.age_range, sd.segment.demographics?.occupation].filter(Boolean).join(' / ')}
                  </p>
                )}
              </div>

              {/* INSIGHT */}
              <div
                className={`ov-ins ${insActualDim ? 'lock-dim' : ''}`}
                style={{ gridRow }}
              >
                {sd.insights.map((ins, ii) => (
                  <div className="ov-ins-i" key={ii}>{ins}</div>
                ))}
                {sd.insights.length === 0 && (
                  <>
                    <div className="ov-ins-i">-</div>
                    <div className="ov-ins-i">-</div>
                    <div className="ov-ins-i">-</div>
                  </>
                )}
              </div>

              {/* Arrow */}
              <div
                className={`ov-arw ${arwActualDim ? 'lock-dim' : ''}`}
                style={{ gridRow }}
              >
                {(sd.insights.length > 0 ? sd.insights : ['-', '-', '-']).map((_, ii) => (
                  <div className="ov-arw-i" key={ii}>&#9654;</div>
                ))}
              </div>

              {/* WHAT */}
              <div
                className={`ov-wht ${whtActualDim ? 'lock-dim' : ''}`}
                style={{ gridRow }}
              >
                {sd.strategies.length > 0 ? (
                  sd.strategies.map((st, sti) => (
                    <div className="ov-wht-i" key={sti}>
                      <strong>{st.name || ''}</strong>
                      {!dimmed && <span>{st.direction || ''}</span>}
                    </div>
                  ))
                ) : (
                  <>
                    <div className="ov-wht-i"><strong>-</strong></div>
                    <div className="ov-wht-i"><strong>-</strong></div>
                    <div className="ov-wht-i"><strong>-</strong></div>
                  </>
                )}
              </div>
            </React.Fragment>
          );
        })}

        {/* Highlight boxes */}
        {highlightColumn === 'who' && (
          <div
            className="ov-highlight"
            style={{ gridColumn: '5', gridRow: '3/8' }}
          />
        )}
        {highlightColumn === 'insight-what' && highlightSegmentRow !== undefined && (
          <div
            className="ov-highlight"
            style={{ gridColumn: '6/11', gridRow: String(segGridRows[highlightSegmentRow]) }}
          />
        )}

        {/* Lock overlay for locked segments */}
        {showLockOverlay && segments.length > 1 && (
          <>
            {/* Highlight box around segment 1 */}
            <div
              className="ov-highlight"
              style={{ gridColumn: '5/11', gridRow: '3' }}
            />
            {/* Lock pill overlay */}
            <div
              style={{
                gridColumn: '5/-1',
                gridRow: '5/8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              <div
                style={{
                  background: '#1D1926',
                  color: '#fff',
                  borderRadius: '9999px',
                  padding: '12px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z" />
                </svg>
                残りのターゲット戦略は無料登録で公開
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OverviewSlide;
